import {
  AdjustmentsHorizontalIcon,
  ArrowRightIcon,
  BookOpenIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  HeartIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Loading from '../components/ui/Loading';
import apiService from '../services/api';
import { Protocol, ProtocolCategory, ProtocolStatus, SearchParams } from '../types';

const Search: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    status: searchParams.get('status') || '',
    tags: searchParams.get('tags')?.split(',') || [],
  });
  const [sortBy, setSortBy] = useState<
    'relevance' | 'title' | 'createdAt' | 'updatedAt' | 'viewCount'
  >(
    (searchParams.get('sortBy') as
      | 'relevance'
      | 'title'
      | 'createdAt'
      | 'updatedAt'
      | 'viewCount') || 'relevance'
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
    hasNext: false,
    hasPrev: false,
  });

  const categories: {
    value: ProtocolCategory;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
  }[] = [
    { value: 'medical', label: 'Médico', icon: HeartIcon },
    { value: 'surgical', label: 'Quirúrgico', icon: ShieldCheckIcon },
    { value: 'cardiac', label: 'Cardíaco', icon: HeartIcon },
    { value: 'emergency', label: 'Emergencia', icon: ExclamationTriangleIcon },
    { value: 'pediatric', label: 'Pediatría', icon: UserGroupIcon },
    { value: 'geriatric', label: 'Geriatría', icon: ChartBarIcon },
  ];

  const statuses: { value: ProtocolStatus; label: string }[] = [
    { value: 'active', label: 'Activo' },
    { value: 'draft', label: 'Borrador' },
    { value: 'archived', label: 'Archivado' },
    { value: 'deprecated', label: 'Deprecado' },
  ];

  const sortOptions = [
    { value: 'relevance', label: 'Relevancia' },
    { value: 'title', label: 'Título' },
    { value: 'createdAt', label: 'Fecha de Creación' },
    { value: 'updatedAt', label: 'Última Actualización' },
    { value: 'viewCount', label: 'Más Vistos' },
    { value: 'usageCount', label: 'Más Utilizados' },
  ];

  const searchProtocols = useCallback(async () => {
    if (!query.trim() && !filters.category && !filters.status && filters.tags.length === 0) {
      setProtocols([]);
      return;
    }

    setIsLoading(true);
    try {
      const searchParamsData: SearchParams = {
        query: query.trim() || undefined,
        filters: {
          category: (filters.category as ProtocolCategory) || undefined,
          status: (filters.status as ProtocolStatus) || undefined,
          tags: filters.tags.length > 0 ? filters.tags : undefined,
        },
        page: pagination.page,
        limit: pagination.limit,
        sortBy: sortBy === 'relevance' ? 'title' : sortBy,
        sortOrder,
      };

      const result = await apiService.searchProtocols(searchParamsData);
      setProtocols(result.protocols);
      setPagination(result.pagination);
    } catch (error) {
      console.error('Search error:', error);
      setProtocols([]);
    } finally {
      setIsLoading(false);
    }
  }, [query, filters, sortBy, sortOrder, pagination.page, pagination.limit]);

  useEffect(() => {
    searchProtocols();
  }, [searchProtocols]);

  useEffect(() => {
    // Update URL when search parameters change
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (filters.category) params.set('category', filters.category);
    if (filters.status) params.set('status', filters.status);
    if (filters.tags.length > 0) params.set('tags', filters.tags.join(','));
    if (sortBy !== 'relevance') params.set('sortBy', sortBy);
    if (sortOrder !== 'desc') params.set('sortOrder', sortOrder);
    if (pagination.page > 1) params.set('page', pagination.page.toString());

    setSearchParams(params);
  }, [query, filters, sortBy, sortOrder, pagination.page, setSearchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    searchProtocols();
  };

  const handleFilterChange = (key: string, value: string | string[]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSortChange = (newSortBy: string) => {
    setSortBy(newSortBy as 'relevance' | 'title' | 'createdAt' | 'updatedAt' | 'viewCount');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSortOrderToggle = () => {
    setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({ category: '', status: '', tags: [] });
    setQuery('');
    setSortBy('relevance');
    setSortOrder('desc');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getCategoryIcon = (category: string) => {
    const categoryConfig = categories.find(c => c.value === category);
    if (categoryConfig) {
      const Icon = categoryConfig.icon;
      return <Icon className='h-5 w-5' />;
    }
    return <BookOpenIcon className='h-5 w-5' />;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      active: { label: 'Activo', className: 'badge-success' },
      draft: { label: 'Borrador', className: 'badge-warning' },
      archived: { label: 'Archivado', className: 'badge-secondary' },
      deprecated: { label: 'Deprecado', className: 'badge-danger' },
    };

    const config = statusConfig[status] || { label: status, className: 'badge-secondary' };
    return <span className={`badge ${config.className}`}>{config.label}</span>;
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  return (
    <Layout showSidebar={true}>
      <div className='space-y-6'>
        {/* Search Header */}
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6'>
          <h1 className='text-2xl font-bold text-gray-900 mb-4'>Búsqueda de Protocolos</h1>

          {/* Search Form */}
          <form onSubmit={handleSearch} className='space-y-4'>
            <div className='flex gap-4'>
              <div className='flex-1'>
                <Input
                  type='search'
                  placeholder='Buscar protocolos, procedimientos, diagnósticos...'
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  leftIcon={<MagnifyingGlassIcon />}
                  className='w-full'
                />
              </div>
              <Button type='submit' disabled={isLoading}>
                {isLoading ? 'Buscando...' : 'Buscar'}
              </Button>
              <Button
                type='button'
                variant='secondary'
                onClick={() => setShowFilters(!showFilters)}
              >
                <FunnelIcon className='h-4 w-4 mr-2' />
                Filtros
              </Button>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className='border-t border-gray-200 pt-4 space-y-4'>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  {/* Category Filter */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Categoría
                    </label>
                    <select
                      value={filters.category}
                      onChange={e => handleFilterChange('category', e.target.value)}
                      className='input-field'
                    >
                      <option value=''>Todas las categorías</option>
                      {categories.map(category => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>Estado</label>
                    <select
                      value={filters.status}
                      onChange={e => handleFilterChange('status', e.target.value)}
                      className='input-field'
                    >
                      <option value=''>Todos los estados</option>
                      {statuses.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Sort Options */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Ordenar por
                    </label>
                    <div className='flex gap-2'>
                      <select
                        value={sortBy}
                        onChange={e => handleSortChange(e.target.value)}
                        className='input-field flex-1'
                      >
                        {sortOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <Button
                        type='button'
                        variant='ghost'
                        onClick={handleSortOrderToggle}
                        className='px-3'
                      >
                        <AdjustmentsHorizontalIcon className='h-4 w-4' />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className='flex justify-between items-center'>
                  <Button
                    type='button'
                    variant='ghost'
                    onClick={clearFilters}
                    className='text-red-600 hover:text-red-700'
                  >
                    <XMarkIcon className='h-4 w-4 mr-2' />
                    Limpiar Filtros
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Search Results */}
        <div className='space-y-4'>
          {/* Results Header */}
          <div className='flex items-center justify-between'>
            <div className='text-sm text-gray-600'>
              {isLoading ? (
                'Buscando...'
              ) : (
                <>
                  {pagination.total > 0 ? (
                    <>
                      Mostrando {protocols.length} de {pagination.total} resultados
                      {query && (
                        <span className='ml-2'>
                          para "<span className='font-medium'>{query}</span>"
                        </span>
                      )}
                    </>
                  ) : (
                    'No se encontraron resultados'
                  )}
                </>
              )}
            </div>
          </div>

          {/* Results List */}
          {isLoading ? (
            <div className='flex justify-center py-12'>
              <Loading size='lg' text='Buscando protocolos...' />
            </div>
          ) : protocols.length > 0 ? (
            <div className='space-y-4'>
              {protocols.map(protocol => (
                <Link
                  key={protocol._id}
                  to={`/protocols/${protocol._id}`}
                  className='block protocol-card group'
                >
                  <div className='flex items-start justify-between mb-3'>
                    <div className='flex items-center space-x-3'>
                      <div className='flex items-center space-x-2'>
                        {getCategoryIcon(protocol.category)}
                        <span className='text-sm font-medium text-gray-600'>
                          {categories.find(c => c.value === protocol.category)?.label}
                        </span>
                      </div>
                      {getStatusBadge(protocol.status)}
                    </div>
                    <div className='text-sm text-gray-500'>v{protocol.version}</div>
                  </div>

                  <h3 className='text-lg font-semibold text-gray-900 mb-2 group-hover:text-insurance-600 transition-colors'>
                    {protocol.title}
                  </h3>

                  <p className='text-gray-600 text-sm mb-4 line-clamp-2'>{protocol.description}</p>

                  <div className='flex items-center justify-between text-sm text-gray-500'>
                    <div className='flex items-center space-x-4'>
                      <span>{protocol.viewCount} vistas</span>
                      <span>{protocol.usageCount} usos</span>
                      <span>{new Date(protocol.updatedAt).toLocaleDateString('es-ES')}</span>
                    </div>
                    <ArrowRightIcon className='h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity' />
                  </div>

                  {protocol.tags && protocol.tags.length > 0 && (
                    <div className='mt-3 flex flex-wrap gap-2'>
                      {protocol.tags.slice(0, 3).map(tag => (
                        <span key={tag} className='badge badge-secondary text-xs'>
                          {tag}
                        </span>
                      ))}
                      {protocol.tags.length > 3 && (
                        <span className='text-xs text-gray-500'>
                          +{protocol.tags.length - 3} más
                        </span>
                      )}
                    </div>
                  )}
                </Link>
              ))}

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className='flex items-center justify-center space-x-2 pt-8'>
                  <Button
                    variant='secondary'
                    disabled={!pagination.hasPrev}
                    onClick={() => handlePageChange(pagination.page - 1)}
                  >
                    Anterior
                  </Button>

                  <div className='flex items-center space-x-1'>
                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <Button
                          key={page}
                          variant={page === pagination.page ? 'primary' : 'ghost'}
                          onClick={() => handlePageChange(page)}
                          className='w-10 h-10'
                        >
                          {page}
                        </Button>
                      );
                    })}
                  </div>

                  <Button
                    variant='secondary'
                    disabled={!pagination.hasNext}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className='text-center py-12'>
              <DocumentTextIcon className='h-12 w-12 text-gray-400 mx-auto mb-4' />
              <h3 className='text-lg font-medium text-gray-900 mb-2'>
                No se encontraron protocolos
              </h3>
              <p className='text-gray-600 mb-4'>
                Intenta ajustar tus filtros de búsqueda o usar términos diferentes.
              </p>
              <Button variant='primary' onClick={clearFilters}>
                Limpiar Filtros
              </Button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Search;
